import { memo, useEffect, useRef, useState } from "react";
import { Howl } from 'howler';
import wheelAudio from './wheel.mp3';
import config from "../../configs/config";

import "./Wheel.scss";

const Wheel = ({
  segments,
  winningSegment,
  onFinished,
  isOnlyOnce = true,
  size = 290,
  upDuration = 100,
  downDuration = 1000,
  fontFamily = "proxima-nova",
}) => {
  let currentSegment = "";
  const [isFinished, setFinished] = useState(false);
  const audioElement = useRef(null);
  let timerHandle = 0;
  const timerDelay = segments.length;
  let angleCurrent = 0;
  let angleDelta = 0;
  let canvasContext = null;
  let maxSpeed = Math.PI / `${segments.length}`;
  const upTime = segments.length * upDuration;
  const downTime = segments.length * downDuration;
  let spinStart = 0;
  let frames = 0;
  const centerX = 300;
  const centerY = 300;

  useEffect(() => {
    let timer;
    wheelInit();
    if (config?.playSoundOnWheelSpin) {
      audioElement.current = new Howl({
        src: [wheelAudio],
        volume: 1,
      });
      audioElement.current?.play();
    }
    timer = setTimeout(spin, 1500);
    return () => clearTimeout(timer);
  }, []);
  const wheelInit = () => {
    initCanvas();
    wheelDraw();
  };

  const initCanvas = () => {
    let canvas = document.getElementById("canvas");
    if (navigator.userAgent.indexOf("MSIE") !== -1) {
      canvas = document.createElement("canvas");
      canvas.setAttribute("width", 1000);
      canvas.setAttribute("height", 600);
      canvas.setAttribute("id", "canvas");
      document.getElementById("wheel").appendChild(canvas);
    }
    canvasContext = canvas.getContext("2d");
  };
  function spin(){
    if (timerHandle === 0) {
      spinStart = new Date().getTime();
      maxSpeed = Math.PI / segments.length;
      frames = 0;
      timerHandle = setInterval(onTimerTick, timerDelay);
    }
  };
  const onTimerTick = () => {
    frames++;
    draw();
    const duration = new Date().getTime() - spinStart;
    let progress = 0;
    let finished = false;
    if (duration < upTime) {
      progress = duration / upTime;
      angleDelta = maxSpeed * Math.sin((progress * Math.PI) / 2);
    } else {
      if (winningSegment) {
        if (currentSegment === winningSegment && frames > segments.length) {
          progress = duration / upTime;
          angleDelta =
            maxSpeed * Math.sin((progress * Math.PI) / 2 + Math.PI / 2);
          progress = 1;
        } else {
          progress = duration / downTime;
          angleDelta =
            maxSpeed * Math.sin((progress * Math.PI) / 2 + Math.PI / 2);
        }
      } else {
        progress = duration / downTime;
        angleDelta =
          maxSpeed * Math.sin((progress * Math.PI) / 2 + Math.PI / 2);
      }
      if (progress >= 1) finished = true;
    }

    angleCurrent += angleDelta;
    while (angleCurrent >= Math.PI * 2) angleCurrent -= Math.PI * 2;
    if (finished) {
      setFinished(true);
      onFinished(currentSegment);
      clearInterval(timerHandle);
      timerHandle = 0;
      angleDelta = 0;
      if (config?.playSoundOnWheelSpin) {
        audioElement.current?.stop();
      }
    }
  };

  const wheelDraw = () => {
    clear();
    drawWheel();
    drawNeedle();
  };

  const draw = () => {
    clear();
    drawWheel();
    drawNeedle();
  };

  const drawSegment = (key, lastAngle, angle) => {
    const ctx = canvasContext;
    const value = segments[key];

    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      0,
      centerX,
      centerY,
      size
    );
    gradient.addColorStop(0, "#F2C7D7");
    gradient.addColorStop(0.401, "#EBBED6");
    gradient.addColorStop(1, "#AB9EE6");
    ctx.fillStyle = gradient;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, size, lastAngle, angle, false);
    ctx.lineTo(centerX, centerY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    const textAngle = (angle + lastAngle) / 2;
    const textX = centerX + Math.cos(textAngle) * (size / 2 + 60);
    const textY = centerY + Math.sin(textAngle) * (size / 2 + 60);
    ctx.save();
    ctx.translate(textX, textY);
    ctx.rotate(textAngle - Math.PI / 2);
    ctx.fillStyle = "#142A58";
    ctx.font = "700 26px " + fontFamily;
    ctx.fillText(value.substr(0, 21), 0, 50);
    ctx.restore();
  };

  const drawWheel = () => {
    const ctx = canvasContext;
    let lastAngle = angleCurrent;
    const len = segments.length;
    const PI2 = Math.PI * 2;
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#C7E0FF";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.font = "1em " + fontFamily;
    for (let i = 1; i <= len; i++) {
      const angle = PI2 * (i / len) + angleCurrent;
      drawSegment(i - 1, lastAngle, angle);
      lastAngle = angle;
    }

    // Draw a center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, 10, 0, PI2, false);
    ctx.closePath();
    ctx.fillStyle = "#C7E0FF";
    ctx.fill();
    ctx.stroke();

    // Draw outer circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, size, 0, PI2, false);
    ctx.closePath();

    ctx.lineWidth = 10;
    ctx.strokeStyle = '#333C88';
    ctx.stroke();
  };

  const drawNeedle = () => {
    const change = angleCurrent + Math.PI / 2;
    let i =
      segments.length -
      Math.floor((change / (Math.PI * 2)) * segments.length) -
      1;
    if (i < 0) i = i + segments.length;
    currentSegment = segments[i];
  };
  const clear = () => {
    const ctx = canvasContext;
    ctx.clearRect(0, 0, 600, 280);
  };
  return (
    <div id="wheel">
      <canvas
        id="canvas"
        width="600"
        height="280"
        style={{
          pointerEvents: isFinished && isOnlyOnce ? "none" : "auto",
        }}
      />
    </div>
  );
};

export default memo(Wheel);
